import { t } from '@/utils/i18n'
import { Container } from '@/components/ui/Container'
import { createIcon } from '@/utils/icons'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'

export function TechnologySection(): HTMLElement {
  const section = document.createElement('section')
  section.className = 'section-container bg-white relative overflow-hidden'

  // Background mesh
  const mesh = document.createElement('div')
  mesh.className = 'absolute inset-0 mesh-bg opacity-30 pointer-events-none'
  section.appendChild(mesh)

  const container = Container({
    children: [
      createSectionHeader(),
      createTechnologyContent(),
    ],
    className: 'relative z-10',
  })

  section.appendChild(container)
  return section
}

function createSectionHeader(): HTMLElement {
  const header = document.createElement('div')
  header.className = 'text-center mb-16'

  const title = document.createElement('h2')
  title.textContent = t('technology.sectionTitle')
  title.className = 'text-4xl sm:text-5xl font-bold text-slate-900 mb-6 tracking-tight'

  const subtitle = document.createElement('p')
  subtitle.textContent = t('technology.sectionSubtitle')
  subtitle.className = 'text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed'

  header.appendChild(title)
  header.appendChild(subtitle)
  return header
}

function createTechnologyContent(): HTMLElement {
  const wrapper = document.createElement('div')
  wrapper.className = 'space-y-12'

  // Full-width video section
  const videoContainer = document.createElement('div')
  videoContainer.className = 'relative w-full glass rounded-3xl p-4 shadow-lg'

  // YouTube video wrapper with aspect ratio
  const videoWrapper = document.createElement('div')
  videoWrapper.className = 'w-full rounded-2xl overflow-hidden aspect-video relative bg-slate-900'

  const iframe = document.createElement('iframe')
  iframe.src = 'https://www.youtube.com/embed/wyyP8oDOCTE?autoplay=1&mute=1&loop=1&playlist=wyyP8oDOCTE&controls=1&rel=0'
  iframe.className = 'absolute inset-0 w-full h-full'
  iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
  iframe.allowFullscreen = true
  iframe.title = 'GIGAette Technology Concept'

  videoWrapper.appendChild(iframe)
  videoContainer.appendChild(videoWrapper)

  // 3-column horizontal layout for sections
  const sectionsGrid = document.createElement('div')
  sectionsGrid.className = 'grid grid-cols-1 md:grid-cols-3 gap-8'

  const sections = [
    {
      title: t('technology.cellStructureTitle'),
      text: t('technology.cellStructureText'),
      icon: 'M6 4v16M10 4v16M14 4v16M18 4v16', // Vertical lines (cells)
    },
    {
      title: t('technology.temperatureDistributionTitle'),
      text: t('technology.temperatureDistributionText'),
      icon: 'M12 12m-3 0a3 3 0 1 0 6 0a3 3 0 1 0-6 0M12 12m-7 0a7 7 0 1 0 14 0a7 7 0 1 0-14 0M12 12m-10 0a10 10 0 1 0 20 0a10 10 0 1 0-20 0', // Concentric circles
    },
    {
      title: t('technology.thermalPistonTitle'),
      text: t('technology.thermalPistonText'),
      icon: 'M4 6h4v12H4zM10 10h6l-3-4v3h7v2h-7v3l3-4H10z', // Piston pushing (horizontal)
    },
  ]

  sections.forEach(section => {
    const itemContent = document.createElement('div')
    itemContent.className = 'flex flex-col items-center text-center space-y-4 h-full'

    const iconContainer = document.createElement('div')
    iconContainer.className = 'w-14 h-14 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center p-3 transition-transform duration-300 group-hover:scale-110'
    iconContainer.appendChild(createIcon(section.icon))

    const titleEl = document.createElement('h4')
    titleEl.textContent = section.title
    titleEl.className = 'text-lg font-bold text-slate-900'

    const textEl = document.createElement('p')
    textEl.textContent = section.text
    textEl.className = 'text-sm text-slate-600 leading-relaxed'

    itemContent.appendChild(iconContainer)
    itemContent.appendChild(titleEl)
    itemContent.appendChild(textEl)

    const card = Card({
      children: itemContent,
      className: 'group h-full hover:border-orange-200',
      hover: true,
      padding: 'lg',
    })

    sectionsGrid.appendChild(card)
  })

  // Note
  const note = document.createElement('p')
  note.textContent = t('technology.thermalPistonNote')
  note.className = 'text-sm text-slate-500 italic text-center max-w-2xl mx-auto'

  const ctaButton = Button({
    children: t('technology.ctaButton'),
    href: '/technology/how-it-works',
    variant: 'primary',
    size: 'lg',
    className: 'mx-auto shadow-glow hover:shadow-glow-hover',
  })

  const ctaContainer = document.createElement('div')
  ctaContainer.className = 'flex justify-center pt-8'
  ctaContainer.appendChild(ctaButton)

  wrapper.appendChild(videoContainer)
  wrapper.appendChild(sectionsGrid)
  wrapper.appendChild(note)
  wrapper.appendChild(ctaContainer)

  return wrapper
}
